import { BuyerListItem } from "@/types/buyerSearch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Building2, Mail, Phone, Pencil, Trash2 } from "lucide-react";

interface BuyerListViewRowProps {
  item: BuyerListItem;
  onViewDetails: () => void;
  onEdit: (item: BuyerListItem) => void;
  onRemove: (buyerId: string) => void;
}

export const BuyerListViewRow = ({ item, onViewDetails, onEdit, onRemove }: BuyerListViewRowProps) => {
  const buyer = item.buyer_profile;
  const isPrivate = item.buyer_type === 'private';
  
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 hover:bg-muted/50 transition-colors">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <Avatar className="h-10 w-10 flex-shrink-0">
          <AvatarImage src={buyer?.profile_image_url} alt={buyer?.full_name} />
          <AvatarFallback>{getInitials(buyer?.full_name || 'N/A')}</AvatarFallback>
        </Avatar>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={onViewDetails}
              className="font-medium text-sm hover:text-primary transition-colors text-left"
            >
              {buyer?.full_name || 'N/A'}
            </button>
            <Badge variant={isPrivate ? "secondary" : "default"} className="text-xs">
              {isPrivate ? "Private" : "Platform"}
            </Badge>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mt-1 text-xs text-muted-foreground">
            {buyer?.email && (
              <a href={`mailto:${buyer.email}`} className="flex items-center gap-1 hover:text-foreground transition-colors">
                <Mail className="h-3 w-3" />
                <span className="truncate">{buyer.email}</span>
              </a>
            )}
            {buyer?.phone_number && (
              <a href={`tel:${buyer.phone_number}`} className="flex items-center gap-1 hover:text-foreground transition-colors">
                <Phone className="h-3 w-3" />
                <span>{buyer.phone_number}</span>
              </a>
            )}
            {buyer?.company_name && (
              <div className="flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                <span className="truncate">{buyer.company_name}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 sm:ml-auto">
        {isPrivate && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(item)}
            className="h-8"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRemove(item.buyer_id)}
          className="h-8 text-destructive hover:text-destructive"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};
